import { AnyAction } from '@reduxjs/toolkit';
import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useMessages2 } from '../hooks/useLanguage';
import { COMPOSE_STAGE, urls } from '../navigation';
import { MaterialQuestionInteraction } from '../view/configurator-q-material';
import { useGetBillQuery, useGetFamiliesQuery, useGetFamilyQuery, useGetVariantsQuery } from './api-slice';
import { calculatePricingRequest } from './billing-helpers';
import { CompositionToViewerContent, CompositionToViewerOptions, CompositionToViewerThumbnailScene, 
         DEFAULT_CAMERA_POSITION, DEFAULT_CAMERA_TARGET, DEFAULT_COMPOSE_CAMERA_POSITION, 
         DEFAULT_COMPOSE_CAMERA_TARGET, THUMBNAILS_SIZE, THUMBNAILS_X_START, THUMBNAILS_X_STEP, 
         THUMBNAILS_Y, compositionToViewer } from './composition-to-viewer';
import { addAttachSelector, animationPositionSelector, configuratorActions, contentSelector, 
         hasLoadedContentSelector, implicitValuesByGroupSelector, modeSelector, questionValuesSelector, 
         sceneSelector, selectedElementSelector, selectedElementsSelector, selectedGroupSelector, 
         selectedImplicitValuesSelector, selectedValuesSelector } from './configurator-slice';
import { useEnvConfig } from './context/environment-context';
import { devActions } from './dev-slice';
import { EventEmitter, eventTypes, events } from './event-emitter';
import { useAppDispatch, useAppSelector } from './hooks';
import { conditionApplies, optionsConditionApplies } from './conditions';
import { sessionIdSelector, useDataRequestLang, useURLLang } from './main-slice';
import { PriceDetails } from './types/api-types';
import { CfgQuestion, CfgQuestionGroup, CfgQuestionValue, InitialCamera } from './types/catalog-types';
import { MAIN_SCENE, OptionValues } from './types/composition';
import { viewerActions } from './viewer-slice';
import { calculateSuitableQuestions, catalogSelector, chosenConfiguratorSelector, 
         countersUseSelector, questionPositionSelector, suitableQuestionsSelector 
       } from './configurator-selectors';

export const DEFAULT_INITIAL_CAMERA: InitialCamera = {
    position: DEFAULT_CAMERA_POSITION,
    target: DEFAULT_CAMERA_TARGET
}

export const DEFAULT_INITIAL_COMPOSE_CAMERA: InitialCamera = {
    position: DEFAULT_COMPOSE_CAMERA_POSITION,
    target: DEFAULT_COMPOSE_CAMERA_TARGET
}

export type CfgViewQuestionSingle = {
    question: CfgQuestion
    values: CfgQuestionValue[]
}

export type CfgViewQuestion = ({
    questionType: 'single',
} & CfgViewQuestionSingle ) | {
    questionType: 'group',
    group: CfgQuestionGroup
    questions: CfgViewQuestionSingle[]
}

export type ConfiguratorViewState = {
    sidebarTitle?: string
    sidebarSteps?: string
    
    currentQuestion?: CfgViewQuestion
    currentStage?: string
    step: number
    questionData?: any
    summaryPrice?: number
    isFetchingBill?: boolean
    differencePrices?: number
    pricesValue?: {
        leatherPrice: number
        fabricPrice: number
    }
    priceCurrency?: string
    summaryText2?: PriceDetails[]
    urlBack: string
    showApplyToAll: boolean
    thumbnailScenes: CompositionToViewerThumbnailScene[]

    canGoNext: boolean
    canGoPrevious: boolean
    applyToAllElements: boolean

    goNext: () => void
    goPrevious: () => void
    onValuesChange: (values: OptionValues, forceApplytoAll: boolean) => void
    onQuestionDataChange: (data: any, interaction: MaterialQuestionInteraction) => void
    goToStage: (stageId: string) => void
    goToSummary: () => void
    goToCompose: () => void;
    onAddElementWithId: (elementId: string) => void
    setApplyToAllElements: (value: boolean) => void

    canGoNextStep: boolean;
    changeCanGoNextStep: (value: boolean) => void;
}


export const ConfiguratorViewContext = createContext<ConfiguratorViewState>({
    sidebarTitle: '',
    
    step: 0,
    urlBack: "",

    canGoNext: false,
    canGoPrevious: false,
    applyToAllElements: true,
    showApplyToAll: false,
    thumbnailScenes: [],

    // actions && callbacks
    goNext: () => {},
    goPrevious: () => {},
    onValuesChange: (values: OptionValues) => {},
    onQuestionDataChange: (data: any) => {},
    goToStage: (stageId: string) => {},
    goToSummary: () => {},
    goToCompose: () => {},
    onAddElementWithId: (elementId: string) => {},
    setApplyToAllElements: (value: boolean) => {},

    canGoNextStep: true,
    changeCanGoNextStep: (value:boolean) => {},
});

export const ConfiguratorLogic = ({ children }: {
    children: ReactNode
}) => {

    // user & navigation info
    const urlLanguage = useURLLang();
    const dataLanguage = useDataRequestLang();
    const dispatch = useAppDispatch();
    const sessionId = useAppSelector(sessionIdSelector);
    const { categoryId, familyId, variantId } = useAppSelector(chosenConfiguratorSelector);
    const { messages } = useMessages2();

    // reference data
    const catalogRoot = useAppSelector(catalogSelector);
    const { qualities, colors, 
            parts, compose, elements, initial, thumbnails, attachProfiles, elementsRoot,
            questions, questionGroups, properties, values } = catalogRoot;
    const { data: families = [] } = useGetFamiliesQuery({lang: dataLanguage, category: categoryId!});

    // content
    const composition = useAppSelector(contentSelector);
    const implicitValuesByGroup = useAppSelector(implicitValuesByGroupSelector);
    const questionValues = useAppSelector(questionValuesSelector);
    const hasLoadedContent = useAppSelector(hasLoadedContentSelector);

    // selection
    const selectedElement = useAppSelector(selectedElementSelector);
    const selectedGroup = useAppSelector(selectedGroupSelector);
    const selectedElements = useAppSelector(selectedElementsSelector);
    const selectedValues = useAppSelector(selectedValuesSelector);
    const selectedImplicitValues = useAppSelector(selectedImplicitValuesSelector);
    
    // status (what to show)
    const mode = useAppSelector(modeSelector);
    const scene = useAppSelector(sceneSelector);
    const [ questionN, nSteps, step, questionData ] = useAppSelector(questionPositionSelector);
    const [ suitableQuestions, minSuitableQuestion, maxSuitableQuestion ] = useAppSelector(suitableQuestionsSelector);
    
    // info for viewer/controls
    const animationPosition = useAppSelector(animationPositionSelector);
    const addAttach = useAppSelector(addAttachSelector);
    const { shownMeshesPerGroup } = useAppSelector(countersUseSelector);
    const { modelsPath, materialsPath, environmentPath } = useEnvConfig();



    // === state

    
    const [ isFirstViewerContent, setIsFirstViewerContent ] = useState(true);
    const [ applyToAllElements, setApplyToAllElements ] = useState(true);
    const [ urlBack, setUrlBack ] = useState("");
    const [ forcePricingRequest, setForcePricingRequest ] = useState(false);
    
    let canGoNextStep: boolean = true;


    // Synchronization with viewer

    useEffect(() => {
        const subscriptionId = EventEmitter.subscribe(eventTypes.VIEWER_EVENTS, (event: string, data: any) => {
            if(event === events.SELECT_EVENT) {
                if(data.type === 'element') {
                    dispatch(configuratorActions.selectElement(data.id));
                } else if(data.type === 'group') {
                    dispatch(configuratorActions.selectGroup(data.id));
                } else if(data.type === 'attach-point') {
                    dispatch(configuratorActions.startAddAttach({
                        elementId: data.elementId,
                        position: data.position,
                        profile: data.profile
                    }));
                } else if(data.type === 'group-rotate') {
                    dispatch(configuratorActions.rotateGroup(data.group, data.element));
                } else if(data.type === 'element-delete') {
                    dispatch(configuratorActions.deleteElement(data.element));
                }
            } else if(event === events.CAMERA_CHANGE) {
                if(data.position && data.target) {
                    dispatch(configuratorActions.setCamera({position: data.position, target: data.target}));
                }
            } else if(event === events.MOVE_GROUP) {
                dispatch(configuratorActions.moveGroup(data));
            }
        });
        return () => {
            EventEmitter.unsubscribe(eventTypes.VIEWER_EVENTS, subscriptionId);
        }
    }, []);

    useEffect(() => {
        EventEmitter.dispatch(eventTypes.VIEW_CONTROLS, events.SET_CURRENT_SCENE_EVENT, scene);
    }, [ scene ]);




    useEffect(() => {
        const question = suitableQuestions?.find(q => q.order === questionN);
        const payload = question ? makeSetCurrentQuestionPayload( questionN, question, step) : undefined;
        if(payload) {
            if(
                ( payload?.question !== questionN ) || 
                ( payload?.step !== step ) || 
                ( payload?.nSteps !== nSteps ) || 
                ( payload?.scene ?? MAIN_SCENE !== scene )
            ) {
                dispatch(configuratorActions.setCurrentQuestion(payload));
            }
        }

    },[suitableQuestions])

    useEffect(() => {
        if(!hasLoadedContent && initial?.elements) {
            const initialGroups = initial.elements.map(element => {
                return {
                    elements: [{
                        element: element.element
                    }],
                    position: element.position ?? [0,0,0],
                    showIf: element.showif,
                    scene: element.scene,
                    initial: true
                };
            })
            dispatch(configuratorActions.setGroups(initialGroups));
        }
        if(initial?.animationStart) {
            dispatch(configuratorActions.setAnimationPosition(
                Math.min(100, Math.max(0, initial.animationStart)))
            );
        } else {
            dispatch(configuratorActions.setAnimationPosition(0));
        }
    }, [ hasLoadedContent, initial, dispatch ]);

    const nCompositionGroups = useMemo(() => {
        return composition.groups.filter(g => g.scene === MAIN_SCENE && (g.showIf ? conditionApplies(g.showIf, selectedValues, qualities, selectedElements) : true)).length;
    }, [ composition.groups, selectedValues, qualities, selectedElements ]);


    const thumbnailScenes: CompositionToViewerThumbnailScene[] = useMemo(() => {
            
        let shownIndex = 0;

        return (thumbnails ?? []).map(t => {
            let show;
            let label;

            if(scene !== MAIN_SCENE || nCompositionGroups === 0) {
                show = false;
            } else if(t.showif) {
                show = optionsConditionApplies(t.showif, selectedValues, composition, catalogRoot, selectedElements);
            } else {
                show = true;
            }

            if(t.quantityProperty) {
                const quantity = Number(selectedValues[t.quantityProperty]);
                if(quantity > 1) {
                    label =`${quantity}\u00D7`;
                }
            }

            const position: [ number, number ] = [ THUMBNAILS_X_START + (shownIndex * THUMBNAILS_X_STEP), THUMBNAILS_Y ];
            if(show) {
                shownIndex++;
            }

            return {
                show,
                scene: t.scene,
                camera: t.camera,
                position,
                size: THUMBNAILS_SIZE,
                label
            }
        });    

    }, [thumbnails, nCompositionGroups, selectedValues, composition, catalogRoot, scene, selectedElements ]);



    useEffect(() => {

        const hasCompose = compose ?? false;
        const isCompose = hasCompose && mode === 'compose';
        const isEditing = hasCompose && ( mode === 'compose' || mode === 'options');


        const ctvContent: CompositionToViewerContent = {
            composition,
            thumbnailScenes,
            implicitValuesByGroup,
            selectedElement,
            selectedGroup: selectedGroup?.id
        }
        const ctvOptions: CompositionToViewerOptions = {
            addUIControls: isCompose,
            addAttachControls: isCompose,
            allowElementSelection: isCompose,
            allowGroupSelection: isEditing && !isCompose,
            allowGroupMovement: isCompose,
            showElementDimensions: isCompose,
            showGroupDimensions: isEditing && !isCompose && nCompositionGroups > 1,
            showGlobalDimensions: hasCompose && nCompositionGroups > 0,
            transparentBackground: false
        };

        const viewerContent = compositionToViewer(ctvContent, ctvOptions, shownMeshesPerGroup,
                                                    elementsRoot, parts, qualities, colors,
                                                    modelsPath, materialsPath, environmentPath);
        dispatch(viewerActions.setContent(viewerContent));
        if(isFirstViewerContent) {
            let initialCamera: InitialCamera;
            if(initial?.camera) {
                initialCamera = initial.camera;
            } else {
                initialCamera = isCompose ? DEFAULT_INITIAL_COMPOSE_CAMERA 
                                                : DEFAULT_INITIAL_CAMERA;
            }
            EventEmitter.dispatch(eventTypes.VIEW_CONTROLS, events.SET_CURRENT_SCENE_EVENT, scene );
            EventEmitter.dispatch(eventTypes.VIEW_CONTROLS, events.SWITCH_CAMERA_EVENT, { type: '3d', scene } );
            EventEmitter.dispatch(eventTypes.VIEW_CONTROLS, events.SET_CAMERA_EVENT, { camera: initialCamera, inmediate: true });
            let initialAnimationPosition = initial?.animationStart ?? 0;
            EventEmitter.dispatch(eventTypes.VIEW_CONTROLS, events.SET_ANIMATION_EVENT,
                {
                    sceneId: MAIN_SCENE,
                    percentage: initialAnimationPosition
                });
            setIsFirstViewerContent(false);
        }

    }, [ elementsRoot, parts, qualities, colors, selectedElement, selectedGroup?.id,
         modelsPath, materialsPath, environmentPath, animationPosition, thumbnailScenes,
         composition, mode, dispatch, nCompositionGroups ])        
      
    useEffect(()=> {
        const hasVariant = families.find((item) => item.id === familyId)?.hasVariants; 
        
        if(hasVariant){
            categoryId && setUrlBack(urls.family(urlLanguage, categoryId, familyId))
        } else {
            categoryId && setUrlBack(urls.category(urlLanguage, categoryId))
        }

    },[families, urlLanguage, categoryId, familyId])


        

    const changeCanGoNextStep = (value: boolean) => {
        canGoNextStep = value;
    };

    const goToStage = useCallback((stageId: string) => {
        const firstQuestionInStage = suitableQuestions.find(q => q.stage === stageId);
        if(mode != 'options') {
            dispatch(configuratorActions.setMode('options'));
        }
        if(firstQuestionInStage) {
            const payload = makeSetCurrentQuestionPayload(firstQuestionInStage.order, firstQuestionInStage);
            dispatch(
                configuratorActions.setCurrentQuestion(payload)
            )
        }
    }, [ mode, suitableQuestions, dispatch ] );

    const goToSummary = useCallback(() => {
        dispatch(configuratorActions.setMode('summary'));
        setForcePricingRequest(true);
        if(compose) {
            EventEmitter.dispatch(eventTypes.VIEW_CONTROLS, events.FIT_CONTENT_EVENT, scene);
        } else if(initial?.camera) {
            EventEmitter.dispatch(eventTypes.VIEW_CONTROLS, events.SET_CAMERA_EVENT, { camera: initial.camera, inmediate: false });
        }

    }, [ dispatch ]);

    const goToCompose = useCallback(() => {
        dispatch(configuratorActions.activateCompose());
    }, [ dispatch ]);
    
    const onAddElementWithId = useCallback((elementId: string) => {
        if(addAttach) {
            const elementInCatalog = elements.find(e => e.id === elementId);
            const compatibleProfiles = attachProfiles![addAttach.profile];
            const newAttachPoint = elementInCatalog!.attach!.find(a => compatibleProfiles.includes(a.profile));
            const newPosition = newAttachPoint!.position;
            dispatch(configuratorActions.addAttachedElement({
                existing: addAttach.elementId,
                newCatalogId: elementId,
                oldPosition: addAttach.position,
                newPosition,
                profile: addAttach.profile
            }))
        } else {
            dispatch(configuratorActions.addGroup({
                elements: [
                    {
                        element: elementId
                    }
                ],
                initial: false   
            }))
        }
        dispatch(configuratorActions.endAddElement());
    }, [ addAttach, elements, attachProfiles, dispatch ]);

    // TODO: review this logic of selecting apply to all maybe should be in configurator-slice, not here
    const onValuesChange = useCallback((values: OptionValues, forceApplyToAll: boolean) => {
        if(!selectedGroup || applyToAllElements || forceApplyToAll) {
            dispatch(configuratorActions.setValues({ values }));
        } else {
            dispatch(configuratorActions.setValues({ groupId: selectedGroup.id, values }));
        }        
    }, [ dispatch, selectedGroup, applyToAllElements ]);

    let error = false;
    let sidebarTitle: string | undefined;
    let sidebarSteps: string | undefined;
    let currentQuestion: CfgViewQuestion | undefined;
    let canGoNext: boolean = false;
    let canGoPrevious: boolean = false;

    if(mode === 'compose') {
        sidebarTitle = messages?.compose_pagetitle && messages?.compose_pagetitle;
        canGoNext = true;
        canGoPrevious = false;
    }

    if(mode === 'options') {
        const group = questionGroups.find(g => g.order === questionN);
        if(group) {
            sidebarTitle = messages && (group?.titleKey?.mainKey ? messages[group?.titleKey?.mainKey] : group?.title)
            currentQuestion = {
                questionType: 'group',
                group,
                questions: suitableQuestions.filter(q => q.order === questionN).map(q => {
                    return {
                        question: q,
                        values: values.filter(v => v.question === q.id &&
                                                           (!v.showif || optionsConditionApplies(v.showif, selectedImplicitValues, composition, catalogRoot, selectedElements)))
                    }
                })
            }
        } else {
            const questionSpec = suitableQuestions.find(q => q.order === questionN);
            if(!questionSpec) {
                error = true;
            } else {
                const titlesKeys = questionSpec?.titleKey
                sidebarTitle = messages && (titlesKeys ? messages[titlesKeys?.mainKey] : questionSpec.title)

                if(questionSpec.type === 'material'){
                    if(questionSpec?.tags && questionSpec?.tags?.length > 0 && messages && titlesKeys){
                        const tags = titlesKeys?.tagsTitle
                        const idQuestion: string = questionSpec.id;
                        const selectedTag = questionValues[idQuestion]?.tag ?? questionSpec?.tags[0]
                        const selectedTagKeys = tags && tags[selectedTag]

                        if(selectedTagKeys !== undefined){
                            switch(step){
                                case 0:
                                    questionSpec?.tags.length <= 1 && (sidebarTitle = messages[selectedTagKeys.quality])
                                    break;
                                case 1:
                                    sidebarTitle =  questionSpec?.tags.length > 1 ? messages[selectedTagKeys.quality] : messages[selectedTagKeys.colour]
                                    break;
                                case 2:
                                    sidebarTitle =  messages[selectedTagKeys.colour]
                                    break;
                                default:
                                    break;
                            }
                        }
                    }
                }

                const disabledQuestion = "disabledif" in questionSpec ? optionsConditionApplies(questionSpec?.disabledif, selectedImplicitValues, composition, catalogRoot, selectedElements) : false
                currentQuestion = {
                    questionType: 'single',
                    question: questionSpec,
                    values: values.filter(v => v.question === questionSpec.id &&
                        (!v.showif || optionsConditionApplies(v.showif, selectedImplicitValues, composition, catalogRoot, selectedElements))).map(item => ({
                            ...item, disabled: disabledQuestion ? true :
                                item?.disabledif ? 
                                    (optionsConditionApplies(item?.disabledif, selectedImplicitValues, composition, catalogRoot, selectedElements)) : false
                        }))
                }
                if(nSteps > 1) {
                    sidebarSteps = `${step+1}/${nSteps}`;
                }
            }
        }
        canGoNext = true; // questionN < maxSuitableQuestion || step < nSteps - 1;
        canGoPrevious = questionN > minSuitableQuestion || step > 0 || compose;
    }

    if(mode === 'summary') {
        sidebarTitle = messages?.offer_pagetitle && messages?.offer_pagetitle;
        canGoNext = false;
        canGoPrevious = true;
    }

    const [ billingRequest, canCallBillService, hasOptionValues, cantCallMotive ] = useMemo(() => {
        return calculatePricingRequest(
            categoryId, familyId, variantId,
            sessionId, dataLanguage,
            composition, implicitValuesByGroup,
            properties, qualities, colors, elementsRoot
        )
    }, [ familyId, sessionId, dataLanguage, composition, implicitValuesByGroup, properties, qualities, colors, elementsRoot ]);

    // for dev purposes
    useEffect(() => {
        dispatch(devActions.setPricingInfo({billingRequest, cantCallMotive}));
    }, [ cantCallMotive, billingRequest ])

    const skipBillService = !canCallBillService && !forcePricingRequest;
    
    const { data: bill, isSuccess: isBillSuccess, isFetching: isFetchingBill } = useGetBillQuery(billingRequest, { skip: skipBillService });

    if(isBillSuccess) {
        let isClassicModel = false;
        if(bill && bill.pricecalculationdetails) {
            isClassicModel = bill.pricecalculationdetails.some(d => {
                return d.modelattributes && d.modelattributes.some(
                    a => a.name === 'classicmodel' && a.value === 'yes'
                );
            });
        }
        dispatch(configuratorActions.setIsClassicModel(isClassicModel));
    }

    let summaryPrice: number | undefined = isBillSuccess ? parseFloat(bill.ttl_newprice) : undefined;
    if(summaryPrice === 0) {
        summaryPrice = undefined;
    }

    let pricesValue: {
        leatherPrice: number
        fabricPrice: number
    } | undefined = isBillSuccess ? {
        leatherPrice: parseFloat(bill.ttl_startpriceleather || "0"),
        fabricPrice: parseFloat(bill.ttl_startpricefabric || "0"),
    }: undefined;
    if(pricesValue && pricesValue.leatherPrice === 0 && pricesValue.fabricPrice === 0) {
        pricesValue = undefined;
    }
    let differencePrices = isBillSuccess ? parseFloat(bill.ttl_pricedifference || "0") : undefined;
    let priceCurrency = isBillSuccess ? bill.ttl_currency : undefined;

    const summaryText2 = bill?.pricecalculationdetails

    let currentStage: string | undefined;
    let questionApplyToAll = false;
    if(currentQuestion) {
        const question = currentQuestion.questionType === 'group' ? currentQuestion.questions[0].question : currentQuestion.question;
        currentStage = question.stage;
        questionApplyToAll = question.applyToAll ?? false;
    }

    const showApplyToAll = compose && nCompositionGroups > 1 && !questionApplyToAll;


    if(compose && mode === 'compose'){
        currentStage = COMPOSE_STAGE
    }

    const goNext = useCallback(() => {

        if(mode === 'compose'){
            if(composition.groups.length > 0) {
                dispatch(configuratorActions.deactivateCompose({mode: 'options'}));
            }
            else {
                dispatch(configuratorActions.deactivateCompose({mode: 'summary'}));
            }
        } else {
            if(mode === 'options') {
                if((step < nSteps - 1) && canGoNextStep) {

                    const elseAction = configuratorActions.setCurrentQuestion({
                        question: questionN,
                        nSteps,
                        step: step + 1,
                        scene
                    });

                    if(currentQuestion?.questionType === 'single' && currentQuestion.question.type === 'material' && currentQuestion.question.nullable) {
                        //debugger;
                        const property = currentQuestion.question.properties[0];
                        const explicitValue = selectedValues[property];
                        if(explicitValue === null || explicitValue === undefined) {
                            const [ action, isSummary ] = makeGoNextAction(questionN, maxSuitableQuestion, suitableQuestions);
                            if(isSummary) {
                                setForcePricingRequest(true);
                            }
                            dispatch(action);
                        } else {
                            dispatch(elseAction);
                        }
                    } else {
                        dispatch(elseAction);
                    }

                    

                } else {

                    let _maxSuitableQuestion = maxSuitableQuestion;
                    let _suitableQuestions = suitableQuestions;
                    let _values = selectedValues;

                    if(currentQuestion && currentQuestion.questionType === 'single' && currentQuestion.question.type === 'material' && step === nSteps - 1 && selectedGroup) {
                        const property = currentQuestion.question.properties[0];
                        const implicitValue = implicitValuesByGroup[selectedGroup.id][property];
                        const explicitValue = selectedValues[property];

                        if(explicitValue === null || explicitValue === undefined) {
                            const values: OptionValues = {}
                            for(const p of currentQuestion.question.properties) {
                                values[p] = implicitValue;
                            }

                            _values = Object.assign({}, selectedValues, values);
                            [ _suitableQuestions, , _maxSuitableQuestion ] = calculateSuitableQuestions( _values, composition, catalogRoot, selectedElements );

                            dispatch(configuratorActions.setValues({ groupId: selectedGroup.id, values }));
                        }
                    }

                    const [ action, isSummary ] = makeGoNextAction(questionN, _maxSuitableQuestion, _suitableQuestions);
                    if(isSummary) {
                        setForcePricingRequest(true);
                    }
                    dispatch(action);
                }
            }
        }
        
    }, [ mode, step, nSteps, questionN, suitableQuestions, dispatch, changeCanGoNextStep, selectedElements ]);

    const goPrevious = useCallback(() => {
        if(mode === 'options') {
            if(step > 0) {
                dispatch(configuratorActions.setCurrentQuestion({
                    question: questionN,
                    nSteps,
                    step: step - 1,
                    scene
                }));
            } else {
                canGoPrevious = questionN > minSuitableQuestion || step > 0 || compose;
                if(step === 0 && !!compose && questionN === 0){
                    dispatch(configuratorActions.activateCompose())
                } else {
                    let prevQuestionN = questionN - 1;
                    let question;
                    while(question === undefined && prevQuestionN >= minSuitableQuestion) {
                        question = suitableQuestions.find(q => q.order === prevQuestionN);
                        if(question === undefined) {
                            prevQuestionN--;
                        }
                    }
                    if(question) {
                        dispatch(
                            configuratorActions.setCurrentQuestion(
                                makeSetCurrentQuestionPayload(prevQuestionN, question)
                            )
                        );
                    }
                }
            }
        }
    }, [ mode, step, nSteps, questionN, suitableQuestions, dispatch ]);    

    const onQuestionDataChange = useCallback((data: any, interaction: MaterialQuestionInteraction) => {
        dispatch(configuratorActions.setQuestionData(data));
        if(interaction === 'material-selected' || interaction === 'tag-selected') {
            canGoNextStep = true
            goNext();
        }
    }, [ dispatch, goNext, changeCanGoNextStep ]);


    const viewState: ConfiguratorViewState = useMemo(() => ({
        
        sidebarTitle, sidebarSteps,
        currentQuestion, currentStage,
        step, questionData,

        summaryPrice, pricesValue, priceCurrency,
        summaryText2, isFetchingBill,
        differencePrices,
        urlBack,
        showApplyToAll, thumbnailScenes,

        canGoNext, canGoPrevious,
        applyToAllElements,

        goNext,
        goPrevious,
        onValuesChange,
        onQuestionDataChange,
        goToStage,
        goToSummary,
        goToCompose,
        canGoNextStep,
        changeCanGoNextStep,
        onAddElementWithId,
        setApplyToAllElements

    }), [
        sidebarTitle, sidebarSteps,
        currentQuestion, currentStage,
        step, questionData,

        summaryPrice, pricesValue, priceCurrency,
        summaryText2, isFetchingBill,
        differencePrices,
        urlBack, showApplyToAll, thumbnailScenes,

        canGoNext, canGoPrevious,

        goNext, goPrevious, onValuesChange, onQuestionDataChange,
        goToStage, goToSummary, goToCompose, canGoNextStep,
        changeCanGoNextStep
    ])

    
    return <ConfiguratorViewContext.Provider value={viewState}>
        { children }
    </ConfiguratorViewContext.Provider>

}

export const useConfiguratorState = () => {
    return useContext(ConfiguratorViewContext);
}

export const useModelName = () => {
    const lang = useDataRequestLang();
    const { familyId, variantId } = useAppSelector(chosenConfiguratorSelector);
    const { data: family, isSuccess: isFamilySuccess } = useGetFamilyQuery({ lang, familyId });
    const { data: variantList } = useGetVariantsQuery({lang, family: familyId!}, {skip: !variantId});

    if(isFamilySuccess && family) {
        const familyName = family.name
        const variantName = variantList?.options.find((item) => item.id === variantId)?.title;
        return `${familyName ?? ""} ${variantName ?? ""}`
    } else {
        return ''
    }
}

// ==== private helpers ====

// TODO instead of returning this, do the pricing request entering the summary
function makeGoNextAction(questionN: number, _maxSuitableQuestion: number, _suitableQuestions: CfgQuestion[]): [ AnyAction, boolean] {
    let nextQuestionN = questionN + 1;
    let question;
    while (question === undefined && nextQuestionN <= _maxSuitableQuestion) {
        question = _suitableQuestions.find(q => q.order === nextQuestionN);
        if (question === undefined) {
            nextQuestionN++;
        }
    }
    if (question) {
        return [
            configuratorActions.setCurrentQuestion(
                makeSetCurrentQuestionPayload(nextQuestionN, question)
            ),
            false
        ];
    } else {        
         return [
            configuratorActions.setMode('summary'),
            true
         ];
    }
}

function makeSetCurrentQuestionPayload(questionN: number, question: CfgQuestion, step?:number): {
    question: number, 
    nSteps: number, 
    step: number,
    scene?: string
} {
    let nSteps = 1;
    if(question.type === 'material'){
        question?.tags && ( question?.tags?.length > 1 ? nSteps = 3 : nSteps = 2 )
    }

    return {
        question: questionN,
        nSteps,
        step: step || 0,
        scene: question.scene
    }
}

